/**
 * Keyboard shortcuts for the website widget (the Chrome extension uses Chrome's
 * own commands instead). Bindings are stored per visitor and editable in Settings.
 *
 * A binding is "alt+shift+KeyR"-style: modifiers + KeyboardEvent.code, so it works
 * across keyboard layouts and with macOS Option producing special characters.
 */
import { N_, t } from '../i18n';

export type ShortcutAction =
  | 'playPause' | 'stop' | 'next' | 'previous' | 'faster' | 'slower'
  | 'toolbar' | 'settings' | 'translate' | 'dictionary' | 'summary' | 'download' | 'clickListen' | 'formReader' | 'help';

export const SHORTCUT_ACTIONS: { id: ShortcutAction; label: string }[] = [
  { id: 'playPause', label: N_('Play / pause') },
  { id: 'stop', label: N_('Stop') },
  { id: 'next', label: N_('Next sentence') },
  { id: 'previous', label: N_('Previous sentence') },
  { id: 'faster', label: N_('Faster') },
  { id: 'slower', label: N_('Slower') },
  { id: 'toolbar', label: N_('Open / close toolbar') },
  { id: 'settings', label: N_('Settings') },
  { id: 'translate', label: N_('Translate') },
  { id: 'dictionary', label: N_('Dictionary') },
  { id: 'summary', label: N_('Summarize') },
  { id: 'download', label: N_('Download MP3') },
  { id: 'clickListen', label: N_('Click and listen on/off') },
  { id: 'formReader', label: N_('Form reader on/off') },
  { id: 'help', label: N_('Toolbar guide') },
];

/**
 * Defaults avoid common browser shortcuts (Alt+←/→ history, Alt+D/Alt+F/Alt+E on
 * Windows Chrome). Arrow actions use Alt+Shift.
 */
export const DEFAULT_SHORTCUTS: Record<ShortcutAction, string> = {
  playPause: 'alt+KeyR',
  stop: 'alt+KeyS',
  next: 'alt+shift+ArrowRight',
  previous: 'alt+shift+ArrowLeft',
  faster: 'alt+shift+ArrowUp',
  slower: 'alt+shift+ArrowDown',
  toolbar: 'alt+KeyT',
  settings: 'alt+KeyG',
  translate: 'alt+KeyL',
  dictionary: 'alt+KeyK',
  summary: 'alt+KeyU',
  download: 'alt+KeyM',
  clickListen: 'alt+KeyC',
  formReader: 'alt+KeyI',
  help: 'alt+KeyH',
};

/** Binding string for a keydown, or null if it's only a modifier / has no modifier */
export function bindingFromEvent(e: KeyboardEvent): string | null {
  if (['Alt', 'Shift', 'Control', 'Meta'].includes(e.key)) return null;
  if (!e.altKey && !e.ctrlKey && !e.metaKey) return null; // require a modifier
  const parts: string[] = [];
  if (e.ctrlKey) parts.push('ctrl');
  if (e.metaKey) parts.push('meta');
  if (e.altKey) parts.push('alt');
  if (e.shiftKey) parts.push('shift');
  parts.push(e.code);
  return parts.join('+');
}

const KEY_NAMES: Record<string, string> = {
  ArrowRight: '→', ArrowLeft: '←', ArrowUp: '↑', ArrowDown: '↓', Space: 'Space',
  Equal: '=', Minus: '-', Comma: ',', Period: '.', Slash: '/',
};

/** Human label, e.g. "Alt + Shift + →" (⌥ on Mac) */
export function formatBinding(binding: string | undefined): string {
  if (!binding) return t('Not set');
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  return binding.split('+').map(p => {
    if (p === 'alt') return mac ? '⌥' : 'Alt';
    if (p === 'shift') return mac ? '⇧' : 'Shift';
    if (p === 'ctrl') return mac ? '⌃' : 'Ctrl';
    if (p === 'meta') return mac ? '⌘' : 'Win';
    if (p.startsWith('Key')) return p.slice(3);
    if (p.startsWith('Digit')) return p.slice(5);
    return KEY_NAMES[p] || p;
  }).join(mac ? '' : ' + ');
}

/** Effective bindings: visitor overrides on top of defaults */
export function effectiveShortcuts(overrides?: Partial<Record<ShortcutAction, string>>): Record<ShortcutAction, string> {
  return { ...DEFAULT_SHORTCUTS, ...(overrides || {}) };
}

/** Typing in a field must never trigger reading shortcuts */
export function isEditableTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || !el.tagName) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName.toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  if (tag === 'input') {
    const type = (el.getAttribute('type') || 'text').toLowerCase();
    return !['button', 'checkbox', 'radio', 'range', 'submit', 'reset', 'color', 'file', 'image'].includes(type);
  }
  return false;
}
